import { Button, Input, toast } from "@/components/kit";
import {
  FieldContent,
  FieldLabel,
  Field as FieldLayout,
} from "@/components/ui/field";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { RISK_THRESHOLDS } from "@/modules/customers/domain/risk-constants";
import {
  useCodRiskSettingsQuery,
  useUpdateCodRiskSettings,
} from "@/modules/customers/presentation/api/queries";
import { useEffect, useState } from "react";

const L = {
  title: "COD / \u0e84\u0ea7\u0eb2\u0ea1\u0eaa\u0ec8\u0e8d\u0e87",
  desc: "\u0e9b\u0eb1\u0e9a\u0ec0\u0e81\u0e99\u0ec1\u0e99\u0eb0\u0e99\u0eb3 COD / \u0ea1\u0eb1\u0e94\u0e88\u0eb3 / \u0ec2\u0ead\u0e99\u0e81\u0ec8\u0ead\u0e99",
  deposit: "\u0ec0\u0e9b\u0eb5\u0e80\u0e8a\u0eb1\u0e99\u0ea1\u0eb1\u0e94\u0e88\u0eb3 (%)",
  newTotal: "\u0e8d\u0ead\u0e94\u0ea5\u0eb9\u0e81\u0e84\u0ec9\u0eb2\u0ec3\u0edd\u0ec8 (\u0e81\u0eb5\u0e9a)",
  depositScore: "\u0e84\u0eb0\u0ec1\u0e99\u0e99 \u0ea1\u0eb1\u0e94\u0e88\u0eb3",
  prepaidScore: "\u0e84\u0eb0\u0ec1\u0e99\u0e99 \u0ec2\u0ead\u0e99\u0e81\u0ec8\u0ead\u0e99",
  stale: "\u0ea1\u0eb7\u0ec9 COD \u0e84\u0ec9\u0eb2\u0e87\u0eae\u0eb1\u0e9a",
  staleHint: "reuse alert_stale_shipped_cod_days",
  save: "\u0e9a\u0eb1\u0e99\u0e97\u0eb6\u0e81",
  loading: "\u0e81\u0eb3\u0ea5\u0eb1\u0e87\u0ec2\u0eab\u0eaa\u0e94...",
  errNum: "\u0e81\u0eb0\u0e99\u0eb4\u0e94\u0e95\u0ec9\u0ead\u0e87\u0ec0\u0e9b\u0eb1\u0e99\u0e95\u0ebb\u0ea7\u0ec0\u0ea5\u0e81",
  ok: "\u0e9a\u0eb1\u0e99\u0e97\u0eb6\u0e81\u0e81\u0eb2\u0e99\u0e95\u0eb1\u0ec9\u0e87\u0e84\u0ec8\u0eb2 COD \u0ec1\u0ea5\u0ec9\u0ea7",
  fail: "\u0e9a\u0eb1\u0e99\u0e97\u0eb6\u0e81\u0e9a\u0ecd\u0ec8\u0eaa\u0eb3\u0ec0\u0ea5\u0eb1\u0e94",
};

export function CodRiskSettingsSection() {
  const canManage = useActionPermission("orders:write");
  const query = useCodRiskSettingsQuery();
  const update = useUpdateCodRiskSettings();

  const [depositPct, setDepositPct] = useState(
    String(RISK_THRESHOLDS.defaultDepositPct),
  );
  const [newCustomerTotal, setNewCustomerTotal] = useState(
    String(RISK_THRESHOLDS.newCustomerTotalLak),
  );
  const [depositScoreMin, setDepositScoreMin] = useState(
    String(RISK_THRESHOLDS.depositScoreMin),
  );
  const [prepaidScoreMin, setPrepaidScoreMin] = useState(
    String(RISK_THRESHOLDS.prepaidScoreMin),
  );
  const [staleDays, setStaleDays] = useState(
    String(RISK_THRESHOLDS.staleShippedCodDays),
  );

  useEffect(() => {
    if (!query.data) return;
    setDepositPct(String(query.data.depositPct));
    setNewCustomerTotal(String(query.data.newCustomerTotalThreshold));
    setDepositScoreMin(String(query.data.depositScoreMin));
    setPrepaidScoreMin(String(query.data.prepaidScoreMin));
    setStaleDays(String(query.data.staleShippedCodDays));
  }, [query.data]);

  function handleSave() {
    const body = {
      depositPct: Number.parseInt(depositPct, 10),
      newCustomerTotalThreshold: Number.parseInt(newCustomerTotal, 10),
      depositScoreMin: Number.parseInt(depositScoreMin, 10),
      prepaidScoreMin: Number.parseInt(prepaidScoreMin, 10),
      staleShippedCodDays: Number.parseInt(staleDays, 10),
    };

    if (
      !Number.isFinite(body.depositPct) ||
      !Number.isFinite(body.newCustomerTotalThreshold) ||
      !Number.isFinite(body.depositScoreMin) ||
      !Number.isFinite(body.prepaidScoreMin) ||
      !Number.isFinite(body.staleShippedCodDays)
    ) {
      toast.error(L.errNum);
      return;
    }

    update.mutate(body, {
      onSuccess: () => toast.success(L.ok),
      onError: (e) =>
        toast.error(L.fail, {
          description: e instanceof Error ? e.message : undefined,
        }),
    });
  }

  if (query.isLoading) {
    return <p className="text-muted-foreground text-sm">{L.loading}</p>;
  }

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <div>
        <h3 className="font-display text-lg">{L.title}</h3>
        <p className="text-muted-foreground text-sm">{L.desc}</p>
      </div>

      <FieldLayout>
        <FieldLabel>{L.deposit}</FieldLabel>
        <FieldContent>
          <Input
            type="number"
            min={1}
            max={100}
            value={depositPct}
            onChange={(e) => setDepositPct(e.target.value)}
            disabled={!canManage}
          />
        </FieldContent>
      </FieldLayout>

      <FieldLayout>
        <FieldLabel>{L.newTotal}</FieldLabel>
        <FieldContent>
          <Input
            type="number"
            min={0}
            step={1000}
            value={newCustomerTotal}
            onChange={(e) => setNewCustomerTotal(e.target.value)}
            disabled={!canManage}
          />
        </FieldContent>
      </FieldLayout>

      <div className="grid gap-4 sm:grid-cols-2">
        <FieldLayout>
          <FieldLabel>{L.depositScore}</FieldLabel>
          <FieldContent>
            <Input
              type="number"
              min={0}
              max={100}
              value={depositScoreMin}
              onChange={(e) => setDepositScoreMin(e.target.value)}
              disabled={!canManage}
            />
          </FieldContent>
        </FieldLayout>
        <FieldLayout>
          <FieldLabel>{L.prepaidScore}</FieldLabel>
          <FieldContent>
            <Input
              type="number"
              min={0}
              max={100}
              value={prepaidScoreMin}
              onChange={(e) => setPrepaidScoreMin(e.target.value)}
              disabled={!canManage}
            />
          </FieldContent>
        </FieldLayout>
      </div>

      <FieldLayout>
        <FieldLabel>{L.stale}</FieldLabel>
        <FieldContent>
          <Input
            type="number"
            min={1}
            max={365}
            value={staleDays}
            onChange={(e) => setStaleDays(e.target.value)}
            disabled={!canManage}
          />
          <p className="mt-1 text-muted-foreground text-xs">{L.staleHint}</p>
        </FieldContent>
      </FieldLayout>

      {canManage ? (
        <Button
          className="rounded-full"
          isLoading={update.isPending}
          onClick={handleSave}
        >
          {L.save}
        </Button>
      ) : null}
    </div>
  );
}
